import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient, readError } from "@/lib/api";
import { toast } from "sonner";
import { Users, UserPlus, Trash2, Send, BadgeCheck, Clock } from "lucide-react";

export default function MyArtisansCard() {
  const [contacts, setContacts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", city: "" });
  const [busy, setBusy] = useState(false);

  const load = () =>
    apiClient.get("/me/artisan-contacts").then(({ data }) => setContacts(data.contacts || [])).catch(() => {});

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await apiClient.post("/me/artisan-contacts", form);
      const c = data.contact;
      if (!c.artisan_id) {
        try {
          await apiClient.post("/invitations", { role: "artisan", name: c.name, email: c.email, city: c.city });
          toast.success(`Invitation envoyée à ${c.name} — il recevra un email pour rejoindre Batisur`);
        } catch (err) {
          toast.info(readError(err));
        }
      } else {
        toast.success(`${c.name} est déjà inscrit sur Batisur — invitez-le sur vos missions en 1 clic`);
      }
      setForm({ name: "", email: "", city: "" });
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(readError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await apiClient.delete(`/me/artisan-contacts/${id}`);
      setContacts((p) => p.filter((c) => c.id !== id));
    } catch (err) {
      toast.error(readError(err));
    }
  };

  return (
    <div className="mb-8 border-2 border-[#0A0A0B] bg-white" data-testid="my-artisans-card">
      <div className="border-b-2 border-[#0A0A0B] px-6 py-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-[#FF4D00]" />
          <div className="display text-2xl">Mes artisans</div>
          <span className="mono text-[10px] uppercase tracking-widest text-zinc-500">{contacts.length} contact{contacts.length > 1 ? "s" : ""}</span>
        </div>
        <button onClick={() => setShowForm((s) => !s)} className="btn-batisur flex items-center gap-2 text-sm" data-testid="add-artisan-contact-btn">
          <UserPlus className="w-4 h-4" /> Inviter un artisan
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="border-b-2 border-zinc-100 px-6 py-5 grid sm:grid-cols-4 gap-3 items-end bg-orange-50/50" data-testid="artisan-contact-form">
          <div>
            <label className="mono text-[10px] uppercase tracking-widest text-zinc-600">Nom / Société</label>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full mt-1 border-2 border-[#0A0A0B] px-3 py-2 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#FF4D00]"
              data-testid="artisan-contact-name" placeholder="Plomberie Martin" />
          </div>
          <div>
            <label className="mono text-[10px] uppercase tracking-widest text-zinc-600">Email</label>
            <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full mt-1 border-2 border-[#0A0A0B] px-3 py-2 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#FF4D00]"
              data-testid="artisan-contact-email" placeholder="contact@artisan.fr" />
          </div>
          <div>
            <label className="mono text-[10px] uppercase tracking-widest text-zinc-600">Ville (optionnel)</label>
            <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
              className="w-full mt-1 border-2 border-[#0A0A0B] px-3 py-2 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#FF4D00]"
              data-testid="artisan-contact-city" placeholder="Lyon" />
          </div>
          <button type="submit" disabled={busy} className="btn-anthracite flex items-center justify-center gap-2 text-sm disabled:opacity-60" data-testid="artisan-contact-submit">
            <Send className="w-4 h-4" /> {busy ? "Envoi…" : "Ajouter & inviter"}
          </button>
          <p className="sm:col-span-4 text-xs text-zinc-600">
            S'il n'est pas encore inscrit, il reçoit un email d'invitation Batisur. S'il est déjà inscrit, vous pourrez l'inviter sur vos missions en 1 clic.
          </p>
        </form>
      )}

      {contacts.length === 0 && !showForm ? (
        <div className="px-6 py-6 text-sm text-zinc-600" data-testid="my-artisans-empty">
          Ajoutez les artisans avec qui vous travaillez déjà : ils seront réinvitables en 1 clic sur chacun de vos chantiers.
        </div>
      ) : (
        <div className="divide-y divide-zinc-100 max-h-72 overflow-auto">
          {contacts.map((c) => (
            <div key={c.id} className="px-6 py-3 flex flex-wrap items-center justify-between gap-3" data-testid={`artisan-contact-${c.id}`}>
              <div>
                <div className="font-semibold text-sm">{c.name}</div>
                <div className="text-xs text-zinc-500">{c.email}{c.city ? ` · ${c.city}` : ""}</div>
              </div>
              <div className="flex items-center gap-3">
                {c.artisan_id ? (
                  <>
                    <span className="mono text-[9px] uppercase tracking-widest px-2 py-1 bg-emerald-50 text-emerald-700 inline-flex items-center gap-1">
                      <BadgeCheck className="w-3 h-3" /> Inscrit
                    </span>
                    <Link to={`/missions/new?invite=${c.artisan_id}`} className="mono text-[10px] uppercase tracking-widest text-[#FF4D00] font-bold hover:underline" data-testid={`contact-new-mission-${c.id}`}>
                      Nouvelle mission →
                    </Link>
                  </>
                ) : (
                  <span className="mono text-[9px] uppercase tracking-widest px-2 py-1 bg-zinc-100 text-zinc-600 inline-flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Invitation envoyée
                  </span>
                )}
                <button onClick={() => remove(c.id)} className="text-zinc-400 hover:text-red-600" data-testid={`contact-delete-${c.id}`} title="Retirer du carnet">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
